// Fire-and-forget API calls for reservation lifecycle.
// Called alongside store actions for immediate UI + background persistence.
// On success, invalidates React Query cache to re-sync from API.

import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-provider";
import { useAuthStore } from "@/store/auth-store";
import { useAfterHoursOverrideStore } from "@/store/after-hours-override-store";
import { useKitchenCapOverrideStore } from "@/store/kitchen-cap-override-store";
import { useAppStore } from "@/store/store";
import { fromApiReservation, originToApi, statusToApi } from "@/lib/adapters";
import { dayjs, parseInTz } from "@/utils/date";
import type { ApiLocation, ApiReservation, ApiReservationPinLevel } from "@/types/api";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUUID(id: string): boolean {
  return UUID_RE.test(id);
}

/**
 * Strip explorer move objects down to what the backend's move schema
 * (``TableMoveSchema``, ``extra="forbid"``) accepts: ``reservation_id`` +
 * ``tables`` only. The explorer carries display-only fields
 * (``fromTableIds`` / ``guestName`` / ``pax``) on each move so the UI can
 * render the relocation banner; forwarding them to any move-applying
 * endpoint (create, update, seat, move-seated, explore/apply) trips a 422.
 * Every payload that ships ``moves`` MUST run them through this.
 */
function serializeMoves(
  moves: ReadonlyArray<{ reservation_id: string; tables: string[] }> | undefined,
): Array<{ reservation_id: string; tables: string[] }> {
  return (moves ?? []).map((m) => ({ reservation_id: m.reservation_id, tables: m.tables }));
}

function invalidate() {
  const organizationId = useAuthStore.getState().organizationId;
  queryClient.invalidateQueries({ queryKey: ["reservations", organizationId] });
  // Every reservation lifecycle event (create, seat, cancel, no-show, …)
  // changes table availability, so the cached suggest-tables results go
  // stale. Invalidating by prefix refetches any *open* modal immediately;
  // a closed modal's cache is dropped on unmount (``gcTime: 0`` on the
  // query) so the next open can't flash a stale suggestion. See
  // ``useSuggestTables``.
  queryClient.invalidateQueries({ queryKey: ["suggest-tables"] });
  // Same reasoning for the explorer drawer: ``TableExplorer`` caches
  // its ``/explore/`` calls with ``staleTime: 30s``, so without an
  // explicit invalidate a second swap within that window shows the
  // pre-swap free options. Prefix invalidate clears every (pax,
  // strategy, …) combo the operator may have browsed.
  queryClient.invalidateQueries({ queryKey: ["explore"] });
  // Same for the inline dry-run banner shown next to the picked
  // mesas in the edit form.
  queryClient.invalidateQueries({ queryKey: ["edit-dry-run"] });
}

/**
 * Broader invalidation for create: the backend may have also materialized a
 * Guest on the fly (``resolve_or_create`` when the payload carried
 * phone/email but no ``guest_id``), so drop the cached guest lists too —
 * otherwise the autocomplete keeps showing stale results until the user
 * refreshes.
 */
function invalidateAfterCreate() {
  const organizationId = useAuthStore.getState().organizationId;
  invalidate();
  queryClient.invalidateQueries({ queryKey: ["guests", organizationId] });
}

function onError(error: unknown) {
  // ky stores the parsed response body on ``error.data``. Prefer the
  // backend's ``detail`` (or top-level string message) so the operator
  // sees the actual reason — "La mesa no acomoda 8 personas" beats
  // "Request failed with status code 409".
  const body = errorBody(error);
  let message: string;
  if (body && typeof body.detail === "string") {
    message = body.detail;
  } else if (body && typeof (body as { message?: unknown }).message === "string") {
    message = (body as { message: string }).message;
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = "Error de conexión";
  }
  useAppStore.getState().notify("error", "Error al sincronizar", message);
  // Also re-fetch to restore consistent state
  invalidate();
}

/**
 * Pull the backend's ``{ detail: "..." }`` message out of a ky HTTPError so we
 * can show the user something actionable (e.g. ``Location Chapinero is not
 * open for 09:24:00-12:24:00 on 2026-04-28``) instead of the generic
 * ``Request failed with status 422``.
 */
/** Pull the parsed JSON body off a ky ``HTTPError``.
 *
 *  ky 2.x consumes the response body automatically and stores the parsed
 *  result on ``error.data``; calling ``error.response.clone().json()``
 *  silently fails because the underlying stream is already locked. Always
 *  read through this helper. */
function errorBody(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== "object") return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return null;
  return data as Record<string, unknown>;
}

/**
 * Pull the backend's ``{ detail: "..." }`` message out of a ky HTTPError so we
 * can show the user something actionable (e.g. ``Location Chapinero is not
 * open for 09:24:00-12:24:00 on 2026-04-28``) instead of the generic
 * ``Request failed with status 422``.
 */
export function readErrorDetail(error: unknown): string | null {
  const body = errorBody(error);
  const detail = body?.detail;
  if (typeof detail === "string" && detail.length > 0) return detail;
  return null;
}

/** Detect the structured 409 body emitted by the kitchen-cap soft enforcement.
 *
 *  The backend's ``StructuredHttpError`` handler returns
 *  ``{detail, code: "kitchen_capacity_override_required", context: {...}}``
 *  on staff/agent paths when the operator did not pass
 *  ``acknowledge_kitchen_capacity_override``. We use that body to render
 *  a confirmation dialog and retry on confirm. Returns ``null`` when the
 *  error is something else (network failure, other 4xx, missing body). */
/** Detect the structured 409 body emitted by the staff after-hours
 *  extension flow. Mirrors :func:`readKitchenCapOverride`. */
function readAfterHoursOverride(error: unknown): {
  slot_local_time: string;
  slot_local_ends_time: string;
  slot_local_date: string;
  public_close_time: string | null;
  extension_minutes: number;
  starts_after_close: boolean;
} | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "after_hours_override_required") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx) return null;
  return {
    slot_local_time: String(ctx.slot_local_time),
    slot_local_ends_time: String(ctx.slot_local_ends_time ?? ""),
    slot_local_date: String(ctx.slot_local_date),
    public_close_time: ctx.public_close_time == null ? null : String(ctx.public_close_time),
    extension_minutes: Number(ctx.extension_minutes),
    starts_after_close: Boolean(ctx.starts_after_close),
  };
}

function readKitchenCapOverride(error: unknown): {
  cap: number;
  current_pax: number;
  requested_pax: number;
  would_be: number;
  slot_local_time: string;
  slot_local_date: string;
} | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "kitchen_capacity_override_required") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx) return null;
  return {
    cap: Number(ctx.cap),
    current_pax: Number(ctx.current_pax),
    requested_pax: Number(ctx.requested_pax),
    would_be: Number(ctx.would_be),
    slot_local_time: String(ctx.slot_local_time),
    slot_local_date: String(ctx.slot_local_date),
  };
}

function readTableConstraintsOverride(
  error: unknown,
): import("@/store/table-constraints-override-store").TableConstraintsContext | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "table_constraints_override_required") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx || !Array.isArray(ctx.issues)) return null;
  return { issues: ctx.issues as never };
}

/** Detect the structured 409 body emitted when force-moving a SEATED
 *  party onto a table occupied by an overlapping reservation. The
 *  backend returns ``code: table_occupied_override_required`` with the
 *  conflicts and whether shortening the moved party is viable. We render
 *  the choice dialog and retry with ``occupied_override``. Mirrors
 *  :func:`readPinOverride`. */
function readOccupiedOverride(
  error: unknown,
): import("@/store/occupied-override-store").OccupiedOverrideContext | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "table_occupied_override_required") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx || !Array.isArray(ctx.conflicts)) return null;
  return {
    conflicts: ctx.conflicts.map((item) => {
      const obj = item as Record<string, unknown>;
      return {
        reservation_id: String(obj.reservation_id),
        guest_name: obj.guest_name == null ? "" : String(obj.guest_name),
        starts_at: String(obj.starts_at),
        ends_at: String(obj.ends_at),
        table_names: Array.isArray(obj.table_names) ? obj.table_names.map(String) : [],
      };
    }),
    can_shorten: Boolean(ctx.can_shorten),
    shorten_to: ctx.shorten_to == null ? null : String(ctx.shorten_to),
  };
}

function readRevertTablesTaken(error: unknown): {
  detail: string;
  suggestions: import("@/store/revert-tables-store").RevertTablesSuggestion[];
} | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "tables_taken_on_revert") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx || !Array.isArray(ctx.suggestions)) return null;
  return {
    detail: String(body.detail ?? ""),
    suggestions: ctx.suggestions as never,
  };
}

function removeLocalReservation(localId: string) {
  useAppStore.setState((state) => ({
    reservations: state.reservations.filter((r) => r.id !== localId),
  }));
}

/**
 * Shared override-cascade retry. Every endpoint that mutates the floor
 * — create, seat, move-seated, explore-apply, waitlist seat — can raise
 * the same structured 409s (after-hours, kitchen-cap, pin, table
 * constraints). This helper centralises the "read code → open the
 * matching dialog store → on confirm, retry with the ack flag set"
 * sequence so each call site stays a single ``attempt(body)`` line.
 *
 * Returns the awaited ``attempt`` result on success, or ``undefined``
 * when the operator dismissed one of the override dialogs. Non-override
 * errors re-throw so the caller can show its own failure toast.
 *
 * Each call site supplies a small ``onDismiss(kind)`` hook to (a) clear
 * any optimistic local state, (b) surface a friendlier "X cancelado"
 * toast worded to that flow, and (c) fire whatever bookkeeping callback
 * the caller already runs on cancel.
 */
type OverrideKind = "after_hours" | "kitchen" | "pin" | "constraints";

export async function withOverrideRetries<T>(
  attempt: (body: Record<string, unknown>) => Promise<T>,
  body: Record<string, unknown>,
  options: { onDismiss?: (kind: OverrideKind) => void } = {},
): Promise<T | undefined> {
  try {
    return await attempt(body);
  } catch (error) {
    const afterHours = readAfterHoursOverride(error);
    if (afterHours) {
      const ok = await useAfterHoursOverrideStore.getState().open(afterHours);
      if (!ok) {
        options.onDismiss?.("after_hours");
        return undefined;
      }
      return withOverrideRetries(
        attempt,
        { ...body, acknowledge_after_hours_override: true },
        options,
      );
    }
    const kitchen = readKitchenCapOverride(error);
    if (kitchen) {
      const ok = await useKitchenCapOverrideStore.getState().open(kitchen);
      if (!ok) {
        options.onDismiss?.("kitchen");
        return undefined;
      }
      return withOverrideRetries(
        attempt,
        { ...body, acknowledge_kitchen_capacity_override: true },
        options,
      );
    }
    const pin = readPinOverride(error);
    if (pin) {
      const { usePinOverrideStore } = await import("@/store/pin-override-store");
      const ok = await usePinOverrideStore.getState().open(pin);
      if (!ok) {
        options.onDismiss?.("pin");
        return undefined;
      }
      return withOverrideRetries(attempt, { ...body, acknowledge_pin_override: true }, options);
    }
    const constraints = readTableConstraintsOverride(error);
    if (constraints) {
      const { useTableConstraintsOverrideStore } =
        await import("@/store/table-constraints-override-store");
      const ok = await useTableConstraintsOverrideStore.getState().open(constraints);
      if (!ok) {
        options.onDismiss?.("constraints");
        return undefined;
      }
      return withOverrideRetries(
        attempt,
        { ...body, acknowledge_table_constraints_override: true },
        options,
      );
    }
    throw error;
  }
}

function getCachedLocations(): ApiLocation[] {
  for (const [, data] of queryClient.getQueriesData({ queryKey: ["locations"] })) {
    if (Array.isArray(data) && data.length) return data as ApiLocation[];
  }
  return [];
}

function getLocationId(): string {
  return getCachedLocations()[0]?.id ?? "";
}

function getActiveLocationTimezone(): string | undefined {
  const activeId = useAuthStore.getState().locationId;
  if (!activeId) return undefined;
  return getCachedLocations().find((l) => l.id === activeId)?.timezone || undefined;
}

/** Clamp ``endsAtIso`` so the reservation never crosses midnight in the
 *  location's local timezone. The backend rejects cross-midnight slots
 *  (every per-day query — opening hours, kitchen cap, day summaries —
 *  assumes one calendar day per booking); rather than letting the user
 *  hit a vague 409 we silently shorten to 23:59 local and surface a
 *  toast so they know their tail got trimmed. Returns the original ISO
 *  when no clamp is needed. */
function clampEndsAtToSameLocalDay(
  startsAtIso: string,
  endsAtIso: string,
  locationTz?: string,
): { endsAt: string; wasClamped: boolean } {
  const startsLocal = locationTz ? dayjs(startsAtIso).tz(locationTz) : dayjs(startsAtIso);
  const endsLocal = locationTz ? dayjs(endsAtIso).tz(locationTz) : dayjs(endsAtIso);
  if (startsLocal.format("YYYY-MM-DD") === endsLocal.format("YYYY-MM-DD")) {
    return { endsAt: endsAtIso, wasClamped: false };
  }
  const clamped = startsLocal.hour(23).minute(59).second(0).millisecond(0);
  return { endsAt: clamped.toISOString(), wasClamped: true };
}

function notifyClamp(originalEndIso: string, clampedEndIso: string, locationTz?: string) {
  const fmt = (iso: string) =>
    (locationTz ? dayjs(iso).tz(locationTz) : dayjs(iso)).format("h:mm A");
  useAppStore
    .getState()
    .notify(
      "info",
      "Reserva acortada al cierre del día",
      `Habría terminado a las ${fmt(originalEndIso)} del día siguiente. Se ajustó a 23:59 — no soportamos cruzar medianoche.`,
    );
}

// ── Create ───────────────────────────────────────────────────────

export function apiCreateReservation(
  localId: string,
  data: {
    guestId?: string;
    guestName: string;
    guestPhone?: string;
    guestEmail?: string;
    pax: number;
    date: string;
    time: string;
    endTime?: string;
    tableIds?: string[];
    /** Pre-emptive relocations picked alongside the new reservation
     *  (typically from the explorer's "Con redistribución" bucket).
     *  Backend's ReservationCreateSchema applies them in the same
     *  transaction as the insert. */
    moves?: Array<{ reservation_id: string; tables: string[] }>;
    origin?: string;
    preferredZone?: string;
    internalNotes?: string;
    /** Guest-facing notes (what the customer wrote, or what staff types
     *  in the "Notas del cliente" textarea of the in-sidebar Nueva
     *  reserva surface). Maps to ``notes`` on the backend schema. */
    notes?: string;
    occasion?: string;
    tags?: string[];
    features?: string[];
    isVip?: boolean;
    status?: string;
    /** Quote-request UUID this reservation converts. When set the
     *  backend excludes that quote's active hold from the kitchen-cap
     *  check and, on success, marks the quote converted + links it. */
    quoteRequestId?: string;
  },
  callbacks?: {
    /** Fires after the backend confirms the create (including the
     *  override-and-retry path). The new-reservation surface uses
     *  this to know when it's safe to dismiss; the modal flow ignores
     *  it and dismisses optimistically. */
    onApiSuccess?: () => void;
    /** Fires when the create fails OR the operator dismissed an
     *  override dialog. The surface keeps the draft visible so the
     *  operator can adjust instead of losing what they typed. */
    onApiCancel?: () => void;
  },
) {
  const locationId = getLocationId();
  if (!locationId) return;

  const locationTz = getActiveLocationTimezone();
  // Walk-ins always mean "now" — the optimistic local store captured
  // ``data.date`` / ``data.time`` in the BROWSER's clock for display, and
  // re-parsing them with the location's TZ would shift the UTC instant
  // when the user's TZ differs from the location's. Use the actual UTC
  // instant directly so the backend's "must not be in the past" check sees
  // the real moment of the action.
  const isWalkin = data.origin === "walkin";
  const startsAt = isWalkin
    ? dayjs()
    : parseInTz(`${data.date} ${data.time}`, "YYYY-MM-DD h:mm A", locationTz);
  // For walk-ins the caller may pass an explicit ``endTime`` (e.g. when
  // an upcoming reservation forces an early end). Fall back to the
  // 3 h default only when no override is given.
  const parsedEndTime = data.endTime
    ? parseInTz(`${data.date} ${data.endTime}`, "YYYY-MM-DD h:mm A", locationTz)
    : null;
  const endsAt = parsedEndTime ?? startsAt.add(3, "hour");

  const startsAtIso = startsAt.toISOString();
  const { endsAt: endsAtIso, wasClamped } = clampEndsAtToSameLocalDay(
    startsAtIso,
    endsAt.toISOString(),
    locationTz,
  );
  if (wasClamped) notifyClamp(endsAt.toISOString(), endsAtIso, locationTz);

  const payload: Record<string, unknown> = {
    guest_name: data.guestName,
    pax: data.pax,
    starts_at: startsAtIso,
    ends_at: endsAtIso,
  };

  if (data.guestId) {
    payload.guest_id = data.guestId;
  } else {
    if (data.guestPhone) payload.guest_phone = data.guestPhone;
    if (data.guestEmail) payload.guest_email = data.guestEmail;
  }
  // Only forward table ids the backend can validate as UUIDs. When the
  // store's local auto-assignment picked seed-data ids (like "1", "2"),
  // skip the field entirely and let the backend re-run its own grouping.
  if (data.tableIds?.length) {
    const uuidTables = data.tableIds.filter(isUUID);
    if (uuidTables.length === data.tableIds.length) {
      payload.tables = uuidTables;
    }
  }
  if (data.preferredZone && isUUID(data.preferredZone))
    payload.preferred_zone_id = data.preferredZone;
  if (data.origin) payload.origin = originToApi(data.origin as any);
  if (data.status) payload.status = statusToApi(data.status as any);
  if (data.internalNotes) payload.internal_notes = data.internalNotes;
  if (data.notes) payload.notes = data.notes;
  if (data.occasion && data.occasion !== "none") payload.occasion = data.occasion;
  if (data.tags?.length) payload.tags = data.tags;
  if (data.features?.length) payload.features = data.features;
  if (data.isVip !== undefined) payload.is_vip = data.isVip;
  if (data.moves?.length) payload.moves = serializeMoves(data.moves);
  if (data.quoteRequestId) payload.quote_request_id = data.quoteRequestId;

  // Issue the create. On a kitchen-cap override 409, prompt the operator
  // and retry once with ``acknowledge_kitchen_capacity_override=true``.
  // Any other error rolls back the optimistic local reservation and
  // surfaces the backend ``detail`` via the toast.
  const onSuccess = (apiRes: ApiReservation) => {
    const mapped = fromApiReservation(apiRes, locationTz);
    useAppStore.setState((state) => ({
      reservations: state.reservations.map((r) => (r.id === localId ? { ...r, ...mapped } : r)),
    }));
    if (mapped.tableIds.length === 0) {
      useAppStore
        .getState()
        .notify(
          "info",
          "Reserva creada sin mesa",
          "No cupo en ninguna mesa — ubícala manualmente en el mapa.",
        );
    }
    invalidateAfterCreate();
    callbacks?.onApiSuccess?.();
  };

  const onFailure = async (error: unknown) => {
    removeLocalReservation(localId);
    const detail = readErrorDetail(error);
    const message = detail || (error instanceof Error ? error.message : "Error de conexión");
    useAppStore.getState().notify("error", "No se pudo crear la reserva", message);
    invalidate();
    callbacks?.onApiCancel?.();
  };

  // One attempt → run through the shared override cascade. Each dismissed
  // dialog rolls back the optimistic local row + surfaces a flow-specific
  // toast, then the caller can adjust without losing what they typed.
  const cancelMessages: Record<OverrideKind, string> = {
    after_hours: "Se respetó el horario público.",
    kitchen: "El tope de cocina se respetó.",
    pin: "Se respetaron los pines.",
    constraints: "Selección de mesas no confirmada.",
  };
  withOverrideRetries(
    (body) => api.post("reservations/", { json: body }).json<ApiReservation>(),
    payload,
    {
      onDismiss: (kind) => {
        removeLocalReservation(localId);
        useAppStore.getState().notify("info", "Reserva cancelada", cancelMessages[kind]);
        invalidate();
        callbacks?.onApiCancel?.();
      },
    },
  )
    .then((created) => {
      if (created) onSuccess(created);
    })
    .catch(onFailure);
}

// ── State Transitions ────────────────────────────────────────────

/** Seat a reservation, optionally overriding the mesa or applying
 * pre-emptive moves to free the target. ``options`` defaults to the
 * legacy "sit on existing tables" behaviour.
 *
 * When the moves plan breaks a pin contract, the backend returns a
 * structured ``pin_override_required`` 409 — the operator gets the
 * confirmation modal and we retry once with the ack flag. */
export function apiSeatReservation(
  id: string,
  options?: {
    tables?: string[];
    moves?: Array<{ reservation_id: string; tables: string[]; [extra: string]: unknown }>;
  },
): Promise<unknown> | undefined {
  if (!isUUID(id)) return;
  const attempt = (body: Record<string, unknown>) =>
    api.post(`reservations/${id}/seat/`, { json: body }).json<ApiReservation>().then(invalidate);

  const payload: Record<string, unknown> = {
    tables: options?.tables ?? [],
    moves: serializeMoves(options?.moves),
  };

  return attempt(payload).catch(async (error: unknown) => {
    const pin = readPinOverride(error);
    if (pin) {
      const { usePinOverrideStore } = await import("@/store/pin-override-store");
      const ok = await usePinOverrideStore.getState().open(pin);
      if (!ok) {
        useAppStore.getState().notify("info", "Sentado cancelado", "Se respetaron los pines.");
        throw error;
      }
      return attempt({ ...payload, acknowledge_pin_override: true });
    }
    onError(error);
    throw error;
  });
}

export function apiCompleteReservation(id: string): Promise<unknown> | undefined {
  if (!isUUID(id)) return;
  return api
    .post(`reservations/${id}/complete/`)
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

export function apiCancelReservation(id: string, reason: string): Promise<unknown> | undefined {
  if (!isUUID(id)) return;
  return api
    .post(`reservations/${id}/cancel/`, { json: { reason } })
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

export function apiMarkNoShow(id: string): Promise<unknown> | undefined {
  if (!isUUID(id)) return;
  return api
    .post(`reservations/${id}/no-show/`)
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

async function revertWithTableRetry(
  id: string,
  path: string,
  reservationLabel: string,
): Promise<void> {
  const attempt = (body?: Record<string, unknown>) =>
    api
      .post(`reservations/${id}/${path}/`, body ? { json: body } : undefined)
      .json<ApiReservation>()
      .then(invalidate);

  try {
    await attempt();
  } catch (error: unknown) {
    const taken = readRevertTablesTaken(error);
    if (!taken) {
      onError(error);
      return;
    }
    const { useRevertTablesStore } = await import("@/store/revert-tables-store");
    const choice = await useRevertTablesStore.getState().open({
      detail: taken.detail,
      suggestions: taken.suggestions,
      reservationLabel,
    });
    if (choice === null) {
      useAppStore
        .getState()
        .notify("info", "Reactivación cancelada", "Resuelve antes de reactivar.");
      return;
    }
    try {
      await attempt({ tables: choice.tables });
    } catch (retryError: unknown) {
      onError(retryError);
    }
  }
}

export function apiRevertNoShow(id: string, reservationLabel = "la reserva") {
  if (!isUUID(id)) return;
  void revertWithTableRetry(id, "revert-no-show", reservationLabel);
}

export function apiUndoNoShow(id: string) {
  if (!isUUID(id)) return;
  api
    .post(`reservations/${id}/undo-no-show/`)
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

export function apiRevertCancel(id: string, reservationLabel = "la reserva") {
  if (!isUUID(id)) return;
  void revertWithTableRetry(id, "revert-cancel", reservationLabel);
}

export function apiRevertSeat(id: string) {
  if (!isUUID(id)) return;
  api
    .post(`reservations/${id}/revert-seat/`)
    .then(async (response) => {
      // 204 → walk-in from the waitlist. The server deleted the reservation
      // and put the entry back to ``waiting``; surface that explicitly so
      // the host knows where it went, then refresh caches.
      if (response.status === 204) {
        sileo.success({ title: "Volvió a la cola" });
        invalidate();
        return;
      }
      // 200 → regular SEATED → BOOKED. No toast (matches previous UX).
      await response.json();
      invalidate();
    })
    .catch(onError);
}

export function apiRevertComplete(id: string, reservationLabel = "la reserva") {
  if (!isUUID(id)) return;
  void revertWithTableRetry(id, "revert-complete", reservationLabel);
}

export function apiToggleConfirmed(id: string) {
  if (!isUUID(id)) return;
  api
    .post(`reservations/${id}/toggle-confirmed/`)
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

// ── Update ───────────────────────────────────────────────────────

export function apiUpdateReservation(
  id: string,
  data: Record<string, unknown>,
  options?: { silentErrors?: boolean },
): Promise<ApiReservation | undefined> {
  if (!isUUID(id)) return Promise.resolve(undefined);

  // Strip display-only fields off any redistribute moves the edit drawer
  // attached — the PATCH schema forbids them (same 422 class as
  // explore/apply).
  if (Array.isArray(data.moves)) {
    data = {
      ...data,
      moves: serializeMoves(data.moves as Array<{ reservation_id: string; tables: string[] }>),
    };
  }

  // Auto-cap ends_at to 23:59 local when the operator's edit would
  // push the slot past midnight. Mirrors the create flow — keeps the
  // operator from hitting the vague "Reservations crossing midnight
  // are not supported" 409.
  const locationTz = getActiveLocationTimezone();
  if (typeof data.starts_at === "string" && typeof data.ends_at === "string") {
    const { endsAt, wasClamped } = clampEndsAtToSameLocalDay(
      data.starts_at,
      data.ends_at,
      locationTz,
    );
    if (wasClamped) {
      notifyClamp(data.ends_at, endsAt, locationTz);
      data = { ...data, ends_at: endsAt };
    }
  }

  // PATCH with the override prompt path: when the backend rejects with
  // ``code: kitchen_capacity_override_required``, surface the modal and
  // retry once with ``acknowledge_kitchen_capacity_override=true``.
  // Same pattern ``apiCreateReservation`` uses on the create flow —
  // kept inline here because the update has a simpler success/failure
  // tree (no optimistic local reservation to roll back).
  const attempt = (payload: Record<string, unknown>) =>
    api
      .patch(`reservations/${id}/`, { json: payload })
      .json<ApiReservation>()
      .then((r) => {
        invalidate();
        return r;
      });

  const tryWithEscalation = (payload: Record<string, unknown>): Promise<ApiReservation> =>
    attempt(payload).catch(async (error: unknown) => {
      const afterHours = readAfterHoursOverride(error);
      if (afterHours) {
        const ok = await useAfterHoursOverrideStore.getState().open(afterHours);
        if (!ok) {
          useAppStore
            .getState()
            .notify("info", "Edición cancelada", "Se respetó el horario público.");
          throw error;
        }
        return tryWithEscalation({ ...payload, acknowledge_after_hours_override: true });
      }
      const kitchen = readKitchenCapOverride(error);
      if (kitchen) {
        const ok = await useKitchenCapOverrideStore.getState().open(kitchen);
        if (!ok) {
          useAppStore
            .getState()
            .notify("info", "Edición cancelada", "El tope de cocina se respetó.");
          throw error;
        }
        return tryWithEscalation({ ...payload, acknowledge_kitchen_capacity_override: true });
      }
      const pin = readPinOverride(error);
      if (pin) {
        const { usePinOverrideStore } = await import("@/store/pin-override-store");
        const ok = await usePinOverrideStore.getState().open(pin);
        if (!ok) {
          useAppStore.getState().notify("info", "Edición cancelada", "Se respetaron los pines.");
          throw error;
        }
        return tryWithEscalation({ ...payload, acknowledge_pin_override: true });
      }
      const constraints = readTableConstraintsOverride(error);
      if (constraints) {
        const { useTableConstraintsOverrideStore } =
          await import("@/store/table-constraints-override-store");
        const ok = await useTableConstraintsOverrideStore.getState().open(constraints);
        if (!ok) {
          useAppStore
            .getState()
            .notify("info", "Edición cancelada", "Selección de mesas no confirmada.");
          throw error;
        }
        return tryWithEscalation({ ...payload, acknowledge_table_constraints_override: true });
      }
      if (!options?.silentErrors) onError(error);
      else invalidate();
      throw error;
    });

  return tryWithEscalation(data);
}

export function apiReassignTables(id: string, tableIds: string[]) {
  if (!isUUID(id)) return;
  api
    .patch(`reservations/${id}/`, { json: { tables: tableIds } })
    .json<ApiReservation>()
    .then(invalidate)
    .catch(onError);
}

/** Change a reservation's ``pin_level`` (none / zone / table).
 *
 * Returns the awaited promise so the caller can react to validation
 * errors (e.g. pin=zone when no preferred_zone_id is set) — pin
 * changes are user-driven and the UI surfaces failures inline.
 *
 * Pass ``silentErrors: true`` when the caller renders its own inline
 * error (the detail panel's ⋯ menu does this). */
export function apiSetPinLevel(
  id: string,
  pinLevel: ApiReservationPinLevel,
  options?: { silentErrors?: boolean },
) {
  if (!isUUID(id)) return Promise.resolve(undefined);
  return api
    .post(`reservations/${id}/pin/`, { json: { pin_level: pinLevel } })
    .json<ApiReservation>()
    .then((r) => {
      invalidate();
      return r;
    })
    .catch((error) => {
      if (!options?.silentErrors) onError(error);
      else invalidate();
      throw error;
    });
}

export interface DryRunRequest {
  pax: number;
  starts_at: string;
  ends_at: string;
  preferred_zone_id?: string | null;
  features?: string[];
  table_ids?: string[];
  exclude_reservation_id?: string | null;
  acknowledge_after_hours_override?: boolean;
}

export interface DryRunSuggestion {
  table_ids: string[];
  total_min: number;
  total_max: number;
  wasted_max: number;
  zone_id: string | null;
  requires_move: {
    reservation_id: string;
    from_table_ids: string[];
    to_table_ids: string[];
  } | null;
}

export interface DryRunResult {
  would_succeed: boolean;
  suggestions: DryRunSuggestion[];
  reason:
    | "invalid_range"
    | "slot_in_past"
    | "outside_hours_or_closure"
    | "kitchen_cap"
    | "no_table_fit"
    | "specific_table_busy"
    | "capacity_insufficient"
    | null;
  /** Every concurrent issue, primary first. Lets the UI stack
   *  "Mesa pequeña · cocina también saturada" when both apply. */
  reasons: string[];
  context: Record<string, unknown>;
  /** Non-blocking warning surfaced when the slot lands in the staff
   *  after-hours extension window and the caller did NOT pass
   *  ``acknowledge_after_hours_override``. ``null`` means no warning. */
  after_hours: {
    slot_local_time: string;
    slot_local_date: string;
    public_close_time: string | null;
    extension_minutes: number;
  } | null;
}

/** Hit the dry-run endpoint to preview which mesa the engine would pick
 * for a hypothetical reservation. Side-effect free — no Guest dedup, no
 * Reservation insert. Used by NewReservationModal to render the auto-
 * pick card with a reason code for empty states. */
export function apiDryRun(payload: DryRunRequest): Promise<DryRunResult> {
  return api.post(`reservations/dry-run/`, { json: payload }).json<DryRunResult>();
}

/** Push a BOOKED reservation forward by ``minutes`` (1..120).
 *
 * Used by the context-menu "+15m / +30m / +45m" quick actions. Returns
 * the awaited promise so the caller can surface a 409 inline
 * (kitchen-cap or table-busy on the shifted window).
 *
 * Pass ``silentErrors: true`` when the caller is already showing an
 * inline error UI for failures — the detail panel does this so the
 * host doesn't see both the raw global toast and the translated amber
 * cartelito at the same time. */
export function apiDelayReservation(
  id: string,
  minutes: number,
  options?: { silentErrors?: boolean },
) {
  if (!isUUID(id)) return Promise.resolve(undefined);
  return api
    .post(`reservations/${id}/delay/`, { json: { minutes } })
    .json<ApiReservation>()
    .then((r) => {
      invalidate();
      return r;
    })
    .catch((error) => {
      if (!options?.silentErrors) onError(error);
      else invalidate();
      throw error;
    });
}

/** Mid-service move: relocate a SEATED party to new tables.
 *
 * The backend requires ``pin_level != "table"`` — the caller must unpin
 * first via ``apiSetPinLevel``. After the move the booking is auto-
 * repinned to ``table`` on the new tables.
 *
 * ``moves`` lets the operator pick a target mesa held by another
 * BOOKED reservation; the explorer's redistribute plan moves those
 * out first inside the same backend transaction. Multi-step chains
 * supported — apply order is left-to-right. */
export function apiMoveSeated(
  id: string,
  tableIds: string[],
  moves: Array<{ reservation_id: string; tables: string[] }> = [],
) {
  if (!isUUID(id)) return Promise.resolve(undefined);
  const attempt = (body: Record<string, unknown>) =>
    api
      .post(`reservations/${id}/move-seated/`, { json: body })
      .json<ApiReservation>()
      .then((r) => {
        invalidate();
        return r;
      });

  const payload: Record<string, unknown> = { tables: tableIds, moves: serializeMoves(moves) };
  const handle = (body: Record<string, unknown>): Promise<ApiReservation> =>
    attempt(body).catch(async (error: unknown) => {
      const pin = readPinOverride(error);
      if (pin) {
        const { usePinOverrideStore } = await import("@/store/pin-override-store");
        const ok = await usePinOverrideStore.getState().open(pin);
        if (!ok) {
          useAppStore.getState().notify("info", "Mesa no cambiada", "Se respetaron los pines.");
          throw error;
        }
        return handle({ ...body, acknowledge_pin_override: true });
      }
      const occupied = readOccupiedOverride(error);
      if (occupied) {
        const { useOccupiedOverrideStore } = await import("@/store/occupied-override-store");
        const choice = await useOccupiedOverrideStore.getState().open(occupied);
        if (choice === null) {
          useAppStore
            .getState()
            .notify("info", "Movimiento cancelado", "La mesa sigue ocupada más tarde.");
          throw error;
        }
        return handle({ ...body, occupied_override: choice });
      }
      onError(error);
      throw error;
    });
  return handle(payload);
}

export interface ExplorerMove {
  reservation_id: string;
  from_table_ids: string[];
  to_table_ids: string[];
  /** Server-resolved labels so consumers can render
   *  "Mover Natalia (5p)" without fetching the moved reservation
   *  locally. Backwards-compat: greedy callers may omit. */
  guest_name?: string;
  pax?: number;
}

export interface ExplorerSuggestion {
  table_ids: string[];
  total_min: number;
  total_max: number;
  wasted_max: number;
  zone_id: string | null;
  /** Feature codes the group carries (intersection of member tables'
   *  features). Used to flag "Sin silla alta" warnings on rows the
   *  backend deliberately surfaces despite a feature gap (e.g. the
   *  "Todas las mesas y combos" bypass section). May be undefined on
   *  legacy responses. */
  features?: string[];
  /** Legacy single-move field (mirrors requires_moves[0]).
   *  Always check ``requires_moves`` for the full chain when applying. */
  requires_move: ExplorerMove | null;
  /** Full move chain. The deep solver emits multi-step plans (A→B,
   *  B→C, …) — sending only the first move corrupts the floor. */
  requires_moves: ExplorerMove[];
}

export interface ExplorerTimingAlt {
  axis: "zone" | "time" | "day";
  starts_at: string;
  ends_at: string;
  zone_id: string | null;
  suggestions: ExplorerSuggestion[];
}

export interface ExplorerDelayedOption {
  table_ids: string[];
  total_min: number;
  total_max: number;
  wasted_max: number;
  zone_id: string | null;
  delay_minutes: number;
}

export interface ExplorerOccupiedConflict {
  reservation_id: string;
  guest_name: string;
  starts_at: string;
  ends_at: string;
  status: string;
}

/** A bookable mesa that is occupied at the slot. Surfaced (when
 *  ``includeAllSingletons`` is requested) so the operator can force a
 *  seated party onto a mesa booked later — picking it routes through
 *  ``apiMoveSeated``, which raises the occupied-override 409 the choice
 *  dialog (force / shorten) handles. */
export interface ExplorerOccupiedSingleton {
  table_id: string;
  table_name: string;
  zone_id: string | null;
  capacity_min: number;
  capacity_max: number;
  conflicts: ExplorerOccupiedConflict[];
}

export interface ExplorerResult {
  free_options: ExplorerSuggestion[];
  free_other_zones: ExplorerSuggestion[];
  redistribute_options: ExplorerSuggestion[];
  /** Redistribute plans whose target lands in a zone OTHER than its
   *  preferred zone. Same element shape as ``redistribute_options``.
   *  Populated only when the "Otras zonas" toggle is on; older backend
   *  responses may omit it. */
  redistribute_other_zones: ExplorerSuggestion[];
  delayed_options: ExplorerDelayedOption[];
  blocked: unknown[];
  timing_alternatives: ExplorerTimingAlt[];
  /** Every single mesa free at the slot, regardless of capacity fit.
   *  Populated when ``includeAllSingletons`` is requested (mid-service /
   *  seat-override flows). Sorted by best-fit. */
  all_singletons: ExplorerSuggestion[];
  /** Bookable mesas occupied at the slot, each with the reservation(s)
   *  holding it. Populated when ``includeAllSingletons`` is requested;
   *  the operator can force a seated party onto one. Older backend
   *  responses may omit it. */
  occupied_singletons?: ExplorerOccupiedSingleton[];
  strategy: string;
}

export interface ExplorerRequest {
  pax: number;
  starts_at: string;
  ends_at: string;
  preferred_zone_id?: string | null;
  features?: string[];
  exclude_reservation_id?: string | null;
  strategy?: "strict" | "zones_flex" | "delays" | "all_flex";
  max_results?: number;
  include_all_singletons?: boolean;
  /** Seating someone who is here: walk-ins-only zones may be offered. */
  for_walk_in?: boolean;
}

/** Hit the explorer endpoint to enumerate seating options.
 *
 * Used by the staff modal's drawer (create flow) and by the reservation
 * card's "Cambiar mesa…" action (reassign flow with
 * ``exclude_reservation_id`` so the booking's own tables aren't counted
 * as busy). */
export function apiExplore(payload: ExplorerRequest): Promise<ExplorerResult> {
  return api.post(`reservations/explore/`, { json: payload }).json<ExplorerResult>();
}

export interface ApplyPlanRequest {
  moves?: Array<{ reservation_id: string; tables: string[] }>;
  target_reservation_id?: string | null;
  target_tables?: string[];
  acknowledge_pin_override?: boolean;
  acknowledge_table_constraints_override?: boolean;
}

/** Detect the structured 409 body emitted when the apply layer needs
 *  an operator confirmation to relocate a pinned booking. Mirrors
 *  :func:`readKitchenCapOverride`. */
function readPinOverride(error: unknown): {
  blocked: Array<{
    reservation_id: string;
    guest_name: string | null;
    pin_level: "zone" | "table";
  }>;
} | null {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  const body = errorBody(error);
  if (!body || body.code !== "pin_override_required") return null;
  const ctx = body.context as Record<string, unknown> | undefined;
  if (!ctx) return null;
  const blockedRaw = ctx.blocked;
  if (!Array.isArray(blockedRaw)) return null;
  return {
    blocked: blockedRaw.map((item) => {
      const obj = item as Record<string, unknown>;
      const pin = String(obj.pin_level) === "table" ? "table" : "zone";
      return {
        reservation_id: String(obj.reservation_id),
        guest_name: obj.guest_name == null ? null : String(obj.guest_name),
        pin_level: pin as "zone" | "table",
      };
    }),
  };
}

/** Apply a plan from /explore/ atomically: optional moves first, then
 * (optional) target reassign. Used by the "Cambiar mesa…" flow when
 * the picked option requires moving another booking.
 *
 * Cascade-with-override: when the backend rejects with a structured
 * ``pin_override_required`` 409, prompt the operator via
 * ``usePinOverrideStore``; on confirm retry once with
 * ``acknowledge_pin_override=true``. Cancellation rejects the
 * original error so the caller's catch path can roll back UI state. */
export function apiApplyPlan(payload: ApplyPlanRequest) {
  // Drop display-only fields the explorer attaches to redistribute moves —
  // the apply schema forbids them. Without this, picking a "Con
  // redistribución" option 422s ("explorer pick failed").
  payload = { ...payload, moves: serializeMoves(payload.moves) };
  const attempt = (body: ApplyPlanRequest) =>
    api
      .post(`reservations/explore/apply/`, { json: body })
      .json<{ items: ApiReservation[] }>()
      .then((res) => {
        invalidate();
        return res.items;
      });

  const handle = (body: ApplyPlanRequest): Promise<ApiReservation[]> =>
    attempt(body).catch(async (error: unknown) => {
      const pin = readPinOverride(error);
      if (pin) {
        const { usePinOverrideStore } = await import("@/store/pin-override-store");
        const ok = await usePinOverrideStore.getState().open(pin);
        if (!ok) {
          useAppStore.getState().notify("info", "Cambio cancelado", "Se respetaron los pines.");
          throw error;
        }
        return handle({ ...body, acknowledge_pin_override: true });
      }
      const constraints = readTableConstraintsOverride(error);
      if (constraints) {
        const { useTableConstraintsOverrideStore } =
          await import("@/store/table-constraints-override-store");
        const ok = await useTableConstraintsOverrideStore.getState().open(constraints);
        if (!ok) {
          useAppStore
            .getState()
            .notify("info", "Cambio cancelado", "Selección de mesas no confirmada.");
          throw error;
        }
        return handle({ ...body, acknowledge_table_constraints_override: true });
      }
      onError(error);
      throw error;
    });

  return handle(payload);
}

/** Swap tables between two reservations atomically via dedicated endpoint. */
export function apiSwapTables(resAId: string, resBId: string) {
  if (!isUUID(resAId) || !isUUID(resBId)) return;
  api
    .post(`reservations/swap-tables/`, {
      json: { reservation_a: resAId, reservation_b: resBId },
    })
    .json()
    .then(invalidate)
    .catch(onError);
}
